/**
 * Logger utility for Hoopoe application
 * Provides structured logging with different levels and automatic storage
 * Logs are persisted to IndexedDB via the debug-log entity
 */

export type LogLevel = "debug" | "info" | "warn" | "error" | "success";

export interface LogEntry {
	id: string;
	timestamp: number;
	level: LogLevel;
	message: string;
	data?: unknown;
	context?: string;
	stackTrace?: string;
}

type LogListener = (entry: LogEntry) => void;

class Logger {
	private listeners: Set<LogListener> = new Set();
	private maxLogs = 1000; // Keep last 1000 logs in memory
	private logs: LogEntry[] = [];
	private isDev: boolean;

	constructor() {
		this.isDev = import.meta.env.MODE === "development";
	}

	/**
	 * Subscribe to log events
	 */
	subscribe(listener: LogListener): () => void {
		this.listeners.add(listener);
		return () => {
			this.listeners.delete(listener);
		};
	}

	/**
	 * Get all stored logs
	 */
	getLogs(): LogEntry[] {
		return [...this.logs];
	}

	/**
	 * Clear all stored logs in memory
	 * Note: To clear persisted logs, use the debug-log entity's useClearAllLogs mutation
	 */
	clearLogs(): void {
		this.logs = [];

		this.notifyListeners({
			id: this.generateId(),
			timestamp: Date.now(),
			level: "info",
			message: "Logs cleared",
			context: "logger",
		});
	}

	/**
	 * Generate unique ID for log entry
	 */
	private generateId(): string {
		return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	}

	/**
	 * Create and store log entry
	 */
	private createLogEntry(
		level: LogLevel,
		message: string,
		data?: unknown,
		context?: string,
	): LogEntry {
		const entry: LogEntry = {
			id: this.generateId(),
			timestamp: Date.now(),
			level,
			message,
			data,
			context,
		};

		// Capture stack trace for errors
		if (level === "error" && data instanceof Error) {
			entry.stackTrace = data.stack;
		}

		// Store log
		this.logs.push(entry);

		// Maintain max logs limit
		if (this.logs.length > this.maxLogs) {
			this.logs = this.logs.slice(-this.maxLogs);
		}

		// Persist log to IndexedDB asynchronously via entity
		if (typeof window !== "undefined") {
			this.persistLog(entry);
		}

		// Notify listeners
		this.notifyListeners(entry);

		return entry;
	}

	/**
	 * Persist log to IndexedDB (async, non-blocking)
	 * Uses setTimeout to ensure this runs in a separate transaction context
	 * to avoid "Transaction is readonly" errors when logging during read operations
	 */
	private async persistLog(entry: LogEntry): Promise<void> {
		// Defer to next tick to escape current transaction context
		setTimeout(async () => {
			try {
				const { getTableDebugLog } = await import(":entities/debug-log");
				await getTableDebugLog().add(entry as any);
			} catch (error) {
				// Silent fail to avoid infinite loop
				// Only log to console, don't try to persist again
				if (error instanceof Error && error.name !== "ReadOnlyError") {
					console.error("Failed to persist log:", error);
				}
			}
		}, 0);
	}

	/**
	 * Notify all listeners of new log entry
	 */
	private notifyListeners(entry: LogEntry): void {
		this.listeners.forEach((listener) => {
			try {
				listener(entry);
			} catch (error) {
				// Prevent listener errors from breaking logging
				console.error("Error in log listener:", error);
			}
		});
	}

	/**
	 * Format log message for console
	 */
	private formatConsoleMessage(entry: LogEntry): string[] {
		const timestamp = new Date(entry.timestamp).toISOString();
		const context = entry.context ? `[${entry.context}]` : "";
		return [`[${timestamp}] ${context} ${entry.message}`, entry.data].filter(
			Boolean,
		) as string[];
	}

	/**
	 * Log debug message (only in development)
	 */
	debug(message: string, data?: unknown, context?: string): void {
		if (!this.isDev) {
			return;
		}

		const entry = this.createLogEntry("debug", message, data, context);
		console.debug(...this.formatConsoleMessage(entry));
	}

	/**
	 * Log info message
	 */
	info(message: string, data?: unknown, context?: string): void {
		const entry = this.createLogEntry("info", message, data, context);
		console.info(...this.formatConsoleMessage(entry));
	}

	/**
	 * Log warning message
	 */
	warn(message: string, data?: unknown, context?: string): void {
		const entry = this.createLogEntry("warn", message, data, context);
		console.warn(...this.formatConsoleMessage(entry));
	}

	/**
	 * Log error message
	 */
	error(message: string, error?: unknown, context?: string): void {
		const entry = this.createLogEntry("error", message, error, context);
		console.error(...this.formatConsoleMessage(entry));
	}

	/**
	 * Log success message
	 */
	success(message: string, data?: unknown, context?: string): void {
		const entry = this.createLogEntry("success", message, data, context);
		console.log(...this.formatConsoleMessage(entry));
	}

	/**
	 * Group related logs
	 */
	group(label: string, collapsed = false): void {
		if (collapsed) {
			console.groupCollapsed(label);
		} else {
			console.group(label);
		}
	}

	/**
	 * End log group
	 */
	groupEnd(): void {
		console.groupEnd();
	}

	/**
	 * Log performance timing
	 */
	time(label: string): void {
		console.time(label);
	}

	/**
	 * End performance timing
	 */
	timeEnd(label: string): void {
		console.timeEnd(label);
	}

	/**
	 * Check if logging is enabled for debugging
	 */
	isDebugEnabled(): boolean {
		if (typeof window === "undefined") {
			return this.isDev;
		}

		try {
			return this.isDev || localStorage.getItem("hoopoe_debug_logs") === "true";
		} catch {
			return this.isDev;
		}
	}
}

// Export singleton instance
export const logger = new Logger();

// Export convenience methods
export const log = {
	debug: (message: string, data?: unknown, context?: string) =>
		logger.debug(message, data, context),
	info: (message: string, data?: unknown, context?: string) =>
		logger.info(message, data, context),
	warn: (message: string, data?: unknown, context?: string) =>
		logger.warn(message, data, context),
	error: (message: string, error?: unknown, context?: string) =>
		logger.error(message, error, context),
	success: (message: string, data?: unknown, context?: string) =>
		logger.success(message, data, context),
	group: (label: string, collapsed?: boolean) => logger.group(label, collapsed),
	groupEnd: () => logger.groupEnd(),
	time: (label: string) => logger.time(label),
	timeEnd: (label: string) => logger.timeEnd(label),
};
